import {
  EYE_LAYER_POLYGONS,
  FACE_MESH_CONTOUR_PATHS,
  IRIS_INDICES
} from '../avatarInspector/featureIndices'
import { FACEMESH_TRIANGULATION } from '../faceMeshTriangulation'

// Derived feature index sets used by avatar engine (built from inspector indices)
export const LIP_FEATURE_INDICES = [
  ...FACE_MESH_CONTOUR_PATHS.lipsOuter,
  ...FACE_MESH_CONTOUR_PATHS.lipsInner
]

export const EYE_FEATURE_INDICES = [
  ...EYE_LAYER_POLYGONS.left,
  ...EYE_LAYER_POLYGONS.right,
  ...IRIS_INDICES.leftRing,
  ...IRIS_INDICES.rightRing,
  IRIS_INDICES.leftCenter,
  IRIS_INDICES.rightCenter
]

export const BROW_FEATURE_INDICES = [
  ...FACE_MESH_CONTOUR_PATHS.leftBrow,
  ...FACE_MESH_CONTOUR_PATHS.rightBrow
]

export const rgba = (r, g, b, a = 0.96) =>
  `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`

export const drawFaceMeshOverlay = (ctx2d, pts, mode = 'tesselation') => {
  if (!ctx2d || !pts?.length) return
  ctx2d.save()
  ctx2d.lineWidth = 0.6
  ctx2d.strokeStyle = 'rgba(0, 220, 255, 0.85)'
  if (mode === 'tesselation') {
    for (let i = 0; i < FACEMESH_TRIANGULATION.length; i += 3) {
      const a = pts[FACEMESH_TRIANGULATION[i]]
      const b = pts[FACEMESH_TRIANGULATION[i + 1]]
      const c = pts[FACEMESH_TRIANGULATION[i + 2]]
      if (!a || !b || !c) continue
      ctx2d.beginPath()
      ctx2d.moveTo(a.x, a.y)
      ctx2d.lineTo(b.x, b.y)
      ctx2d.lineTo(c.x, c.y)
      ctx2d.closePath()
      ctx2d.stroke()
    }
  } else if (mode === 'contours') {
    const drawPath = (arr) => {
      if (!arr?.length) return
      const f = pts[arr[0]]
      if (!f) return
      ctx2d.beginPath()
      ctx2d.moveTo(f.x, f.y)
      for (let i = 1; i < arr.length; i += 1) {
        const p = pts[arr[i]]
        if (!p) continue
        ctx2d.lineTo(p.x, p.y)
      }
      ctx2d.stroke()
    }
    drawPath(FACE_MESH_CONTOUR_PATHS.faceOval)
    drawPath(FACE_MESH_CONTOUR_PATHS.leftEye)
    drawPath(FACE_MESH_CONTOUR_PATHS.rightEye)
    drawPath(FACE_MESH_CONTOUR_PATHS.leftBrow)
    drawPath(FACE_MESH_CONTOUR_PATHS.rightBrow)
    drawPath(FACE_MESH_CONTOUR_PATHS.lipsOuter)
    drawPath(FACE_MESH_CONTOUR_PATHS.lipsInner)
    drawPath(FACE_MESH_CONTOUR_PATHS.noseBridge)
  }
  ctx2d.restore()
}

export const drawRigAlignedLayer = (ctx, layerCanvas, avatarRig, liveMetrics, alpha = 1) => {
  if (!ctx || !layerCanvas || !avatarRig?.faceMetrics || !liveMetrics) return
  const baseFaceW = Math.max(1, avatarRig.faceMetrics.faceW)
  const baseFaceH = Math.max(1, avatarRig.faceMetrics.faceH)
  const drawW = Math.max(8, (layerCanvas.width / baseFaceW) * liveMetrics.faceW)
  const drawH = Math.max(8, (layerCanvas.height / baseFaceH) * liveMetrics.faceH)
  const anchorX = avatarRig.faceMetrics.centerX / Math.max(1, avatarRig.imageWidth)
  const anchorY = avatarRig.faceMetrics.centerY / Math.max(1, avatarRig.imageHeight)
  const x = -drawW * anchorX
  const y = -drawH * anchorY

  ctx.save()
  ctx.translate(liveMetrics.centerX, liveMetrics.centerY)
  if (liveMetrics.rotation) ctx.rotate(liveMetrics.rotation)
  if (alpha < 1) ctx.globalAlpha = alpha
  try {
    if (typeof ctx.imageSmoothingEnabled === 'boolean') ctx.imageSmoothingEnabled = true
    if (typeof ctx.imageSmoothingQuality === 'string') ctx.imageSmoothingQuality = 'high'
  } catch {}
  ctx.drawImage(layerCanvas, x, y, drawW, drawH)
  ctx.restore()
}

export const drawRigAlignedLayerWithYaw = (
  ctx,
  layerCanvas,
  avatarRig,
  liveMetrics,
  yaw = 0,
  alpha = 1,
  offsetY = 0
) => {
  if (!ctx || !layerCanvas || !avatarRig?.faceMetrics || !liveMetrics) return
  const baseFaceW = Math.max(1, avatarRig.faceMetrics.faceW)
  const baseFaceH = Math.max(1, avatarRig.faceMetrics.faceH)
  const drawW = Math.max(8, (layerCanvas.width / baseFaceW) * liveMetrics.faceW)
  const drawH = Math.max(8, (layerCanvas.height / baseFaceH) * liveMetrics.faceH)
  const anchorX = avatarRig.faceMetrics.centerX / Math.max(1, avatarRig.imageWidth)
  const anchorY = avatarRig.faceMetrics.centerY / Math.max(1, avatarRig.imageHeight)
  const x = -drawW * anchorX
  const y = -drawH * anchorY

  const yawMag = Math.max(0, Math.min(1, Math.abs(yaw)))
  const yawSign = Math.sign(yaw) || 1
  const scaleX = Math.max(0.78, 1 - yawMag * 0.22)
  const shiftX = yawSign * yawMag * (liveMetrics.faceW * 0.12)

  ctx.save()
  ctx.translate(liveMetrics.centerX + shiftX, liveMetrics.centerY + (offsetY || 0))
  if (liveMetrics.rotation) ctx.rotate(liveMetrics.rotation)
  ctx.scale(scaleX, 1)
  if (alpha < 1) ctx.globalAlpha = alpha
  try {
    if (typeof ctx.imageSmoothingEnabled === 'boolean') ctx.imageSmoothingEnabled = true
    if (typeof ctx.imageSmoothingQuality === 'string') ctx.imageSmoothingQuality = 'high'
  } catch {}
  ctx.drawImage(layerCanvas, x, y, drawW, drawH)
  ctx.restore()
}

export const drawLayerCentered = (ctx, layerCanvas, liveMetrics, alpha = 1) => {
  if (!ctx || !layerCanvas || !liveMetrics) return
  const drawW = Math.max(8, liveMetrics.faceW)
  const drawH = Math.max(8, liveMetrics.faceH)
  const x = -drawW * 0.5
  const y = -drawH * 0.5
  ctx.save()
  ctx.translate(liveMetrics.centerX, liveMetrics.centerY)
  if (liveMetrics.rotation) ctx.rotate(liveMetrics.rotation)
  if (alpha < 1) ctx.globalAlpha = alpha
  try {
    if (typeof ctx.imageSmoothingEnabled === 'boolean') ctx.imageSmoothingEnabled = true
    if (typeof ctx.imageSmoothingQuality === 'string') ctx.imageSmoothingQuality = 'high'
  } catch {}
  ctx.drawImage(layerCanvas, x, y, drawW, drawH)
  ctx.restore()
}

export const drawFallbackAvatar = ({
  ctx,
  avatarImage,
  avatarRig,
  avatarLayers,
  liveRigMetrics,
  centerX,
  centerY,
  rotation,
  targetW,
  targetH,
  showAvatarFace,
  showAvatarHair,
  buildCutoutByMode,
  debug
} = {}) => {
  if (!ctx || !avatarImage?.img) return false
  const canCutout = typeof buildCutoutByMode === 'function'
  const preferCutout = canCutout && (!avatarRig || avatarLayers?._fallback === true)
  let cutout = null
  if (preferCutout) {
    try {
      cutout = buildCutoutByMode()
    } catch (err) {
      console.debug('[utils] drawFallbackAvatar buildCutout error', err)
    }
  }

  if (cutout) {
    debug && debug('utils: drawFallbackAvatar (cutout)')
    if (avatarRig) {
      drawRigAlignedLayer(ctx, cutout, avatarRig, liveRigMetrics)
    } else {
      // When no face detected, draw cutout at original size (no scaling to face metrics)
      // Scale to fit canvas while preserving aspect ratio
      const cutoutW = cutout.width || 1
      const cutoutH = cutout.height || 1
      const canvasW = ctx.canvas?.width || targetW || cutoutW
      const canvasH = ctx.canvas?.height || targetH || cutoutH

      // Fit cutout within canvas (contain mode)
      const scale = Math.min(canvasW / cutoutW, canvasH / cutoutH)
      const drawW = cutoutW * scale
      const drawH = cutoutH * scale

      ctx.save()
      ctx.translate(centerX || canvasW / 2, centerY || canvasH / 2)
      if (rotation) ctx.rotate(rotation)
      try {
        if (typeof ctx.imageSmoothingEnabled === 'boolean') ctx.imageSmoothingEnabled = true
        if (typeof ctx.imageSmoothingQuality === 'string') ctx.imageSmoothingQuality = 'high'
      } catch {}
      ctx.drawImage(cutout, -drawW / 2, -drawH / 2, drawW, drawH)
      ctx.restore()
    }
    return true
  }

  const fallbackLayer = showAvatarFace
    ? (showAvatarHair && avatarLayers?.headCanvas) || avatarLayers?.faceCanvas
    : showAvatarHair
      ? avatarLayers?.hairCanvas
      : null
  if (fallbackLayer) {
    debug && debug('utils: drawFallbackAvatar (layer)')
    drawRigAlignedLayer(ctx, fallbackLayer, avatarRig, liveRigMetrics)
    return true
  }

  if (showAvatarFace || showAvatarHair) {
    debug && debug('utils: drawFallbackAvatar (image)')
    ctx.save()
    ctx.translate(centerX, centerY)
    if (rotation) ctx.rotate(rotation)
    ctx.drawImage(avatarImage.img, -targetW / 2, -targetH / 2, targetW, targetH)
    ctx.restore()
    return true
  }

  // As a final fallback, try cutout-or-source again centered
  if (canCutout) {
    try {
      const cut = buildCutoutByMode()
      const src = cut || avatarImage?.img || null
      if (src) {
        ctx.save()
        ctx.translate(centerX, centerY)
        if (rotation) ctx.rotate(rotation)
        ctx.drawImage(src, -targetW / 2, -targetH / 2, targetW, targetH)
        ctx.restore()
        return true
      }
    } catch {}
  }
  return false
}

// Canvas helpers used by segmentation and other runtimes
const imageDataCache = new WeakMap()

const getCachedImageData = (canvas, ctx, width, height) => {
  if (!canvas || !ctx) return null
  const cached = imageDataCache.get(canvas)
  if (cached && cached.width === width && cached.height === height && cached.imageData)
    return cached.imageData
  let imageData = null
  try {
    imageData = ctx.createImageData(width, height)
  } catch (e) {
    imageData = null
  }
  if (imageData) {
    imageDataCache.set(canvas, { width, height, imageData })
  }
  return imageData
}

export const ensureCanvasWithImage = (adapter, existingCanvas, width, height, resourceManager) => {
  if (!width || !height) return { canvas: null, ctx: null, imageData: null }
  let canvas = existingCanvas
  let ctx = null
  let imageData = null
  if (!canvas || canvas.width !== width || canvas.height !== height) {
    canvas = adapter.createCanvas(width, height)
    ctx = adapter.getContext(canvas, '2d', { willReadFrequently: true })
    imageData = getCachedImageData(canvas, ctx, width, height)
    if (resourceManager && typeof resourceManager.registerCanvas === 'function') {
      try {
        resourceManager.registerCanvas(canvas)
        if (typeof resourceManager.registerDisposable === 'function') {
          resourceManager.registerDisposable(() => {
            try {
              canvas.width = 0
              canvas.height = 0
            } catch (err) {
              console.debug('[utils] ensureCanvasWithImage dispose error', err)
            }
            canvas = null
          })
        }
      } catch (err) {
        console.debug('[utils] ensureCanvasWithImage register error', err)
      }
    }
  } else {
    ctx = adapter.getContext(canvas, '2d', { willReadFrequently: true })
    imageData = getCachedImageData(canvas, ctx, width, height)
  }
  return { canvas, ctx, imageData }
}

export const writeAlphaIntoImageData = (imageData, width, height, alphaAt) => {
  if (!imageData) return false
  const pixelCount = width * height
  for (let p = 0; p < pixelCount; p += 1) {
    const a = Math.max(0, Math.min(255, Math.round(alphaAt(p) || 0)))
    const di = p * 4
    imageData.data[di] = 255
    imageData.data[di + 1] = 255
    imageData.data[di + 2] = 255
    imageData.data[di + 3] = a
  }
  return true
}

export const putImageDataToCanvas = (ctx, imageData) => {
  if (!ctx || !imageData) return false
  try {
    ctx.putImageData(imageData, 0, 0)
    return true
  } catch (err) {
    console.debug('[utils] putImageDataToCanvas error', err)
    return false
  }
}

export default {
  LIP_FEATURE_INDICES,
  EYE_FEATURE_INDICES,
  BROW_FEATURE_INDICES,
  rgba,
  drawFaceMeshOverlay
}
